import { toMillis } from '@/utils/folder.utils';
import { defaultWorkflowCover } from './defaultCover';
import type { DashboardWorkflow } from '@/types/workflows';
import type { FolderResponseDto } from '@/types/folder.types';

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const valA = a[orderBy];
  const valB = b[orderBy];

  // Handle undefined/null values - put them at the end
  if (valA === undefined || valA === null) {
    return 1;
  }
  if (valB === undefined || valB === null) {
    return -1;
  }

  // Check if the values are strings; if so, compare them in a case-insensitive manner
  if (typeof valA === 'string' && typeof valB === 'string') {
    const aValueLower = valA.toLowerCase();
    const bValueLower = valB.toLowerCase();

    if (bValueLower < aValueLower) {
      return -1;
    }
    if (bValueLower > aValueLower) {
      return 1;
    }
    return 0;
  }

  if (valB < valA) {
    return -1;
  }
  if (valB > valA) {
    return 1;
  }

  return 0;
}

export type ListItem =
  | (DashboardWorkflow & { type: 'recipe'; ownerName?: string; owner?: string })
  | (FolderResponseDto & { type: 'folder' });

export function getTimeColumnSortValue(
  item: { createdAt: string; updatedAt?: string },
  orderBy: 'updatedAt' | 'createdAt',
): number {
  if (orderBy === 'createdAt') {
    return toMillis(item.createdAt) || 0;
  }
  return toMillis(item.updatedAt) || toMillis(item.createdAt);
}

export function getListItemDateSortValue(
  item: ListItem,
  orderBy: 'updatedAt' | 'createdAt',
  /** Subtree rollup for folder "last activity"; folders use their own `createdAt` when sorting by created. */
  folderSubtreeUpdatedMs: (folderId: string) => number,
): number {
  if (item.type === 'folder' && orderBy === 'updatedAt') {
    return folderSubtreeUpdatedMs(item.id);
  }
  return getTimeColumnSortValue(item, orderBy);
}

export const FOLDER_ICON = 'folder' as const;
export type FolderItem = string;

/**
 * @deprecated Use FolderWorkflowData instead
 */
export interface FolderRecipeData {
  [folderId: string]: {
    posters: FolderItem[];
    /** Thumbnail tiles: subfolders plus the rows in the folder. */
    count: number;
    /** Rows in the folder, without subfolders. The workflow label uses this. */
    contentCount?: number;
  };
}

// TODO: copy interface here when done with cleanup
export type FolderWorkflowData = FolderRecipeData;

export function calculateFolderRecipeData(
  folders: FolderResponseDto[],
  workflows: (DashboardWorkflow & { folderId?: string | null })[],
  allWorkflows?: (DashboardWorkflow & { folderId?: string | null })[],
  allFolders?: FolderResponseDto[],
  isFplMigrationEnabled?: boolean,
): FolderWorkflowData {
  const data: FolderWorkflowData = {};

  // The section's rows when given: a folder's count is about its contents, not the current level.
  const workflowsForCounting = allWorkflows || workflows;

  folders.forEach((folder) => {
    const folderWorkflows = workflowsForCounting.filter((w) => w.folderId === folder.id);
    // Use allFolders to find subfolders, not the filtered folders
    const subfolders = (allFolders || folders).filter((f) => f.parentId === folder.id);

    // Combine folder icons and workflow posters
    const items: FolderItem[] = [];

    // Add folder icons first
    subfolders.forEach(() => items.push(FOLDER_ICON));

    // Then add workflow posters
    folderWorkflows.forEach((r) => items.push(r.poster || defaultWorkflowCover(isFplMigrationEnabled)));

    data[folder.id] = {
      posters: items.slice(0, 4),
      count: subfolders.length + folderWorkflows.length,
      contentCount: folderWorkflows.length,
    };
  });

  return data;
}
